import { Injectable, inject } from '@angular/core';
import {
  BaseWebsocketMessage,
  SubWebsocketMessageRequest,
  WebsocketEntTypes,
  WebSocketService,
  WSEntityManager,
} from '@qtek/core/websockets-core';
import { Observable } from 'rxjs';

/**
 * Live subscription to a document's extraction results (SD-2665).
 *
 * <p>The backend stages every extractor's output as a `DocumentMeta` row against
 * the document — term-sheet and credit-agreement fields, and covenants — so this
 * is how the frontend learns extraction finished. Extraction is a multi-minute
 * LLM pass with no synchronous result, so there is nothing to await: subscribe as
 * soon as extraction is requested and let the rows arrive.
 *
 * <p><b>Why not `TermSheetDocMetaService`.</b> That service and its covenant twin
 * subscribe with `ent: 'docmeta'`, the v1 short name. `ws-server-v2` resolves
 * `ent` through `EntityTaskRegistry`, which keys tasks by the JPA entity's simple
 * class name and documents that there is no short-name mapping in v2 — so
 * `docmeta` reaches nothing there. Both carry an MTODO to migrate; this service is
 * the v2 shape they should converge on, not a third variant to keep in step.
 *
 * <p>`documentId` is required, not optional: `DocumentMetaEntityTask.visibleSpec`
 * rejects a request without it. Access is enforced separately — the caller must
 * have a role on the owning deal (or the NDA template's company), or the
 * subscription simply yields nothing.
 */
@Injectable({ providedIn: 'root' })
export class DocumentMetaService {
  private readonly wsService = inject(WebSocketService);

  readonly entityName: WebsocketEntTypes = 'DocumentMeta';
  private readonly wsManager = new WSEntityManager(
    this.wsService,
    this.entityName
  );

  /**
   * Subscribes to extraction results for one document.
   *
   * @param mysid caller-scoped subscription id, so several documents can be
   *   watched at once without their streams colliding
   * @param documentId the document being extracted — the `document._id`, not the
   *   reference id the extraction trigger takes
   */
  subscribeDocumentMeta(
    mysid: string,
    documentId: string,
    payload: Omit<SubWebsocketMessageRequest, 'ent' | 'op' | 'mysid'> = {}
  ): Observable<BaseWebsocketMessage> {
    return this.wsManager.subscribeEntity(
      { ...payload, mysid, prms: { ...payload.prms, documentId }, view: 'pln' },
      true
    );
  }
}
